// 정식 등급이 되는 순간(시작 등급 지정 · 루키 승급 심사 승인) 결제해야 이용 시작 — 「레이블리 시작하기」 페이지(/start, MembershipStart)의 순수 판정·문구
// (대표 지시 2026-10-20 · 화면은 시안 C 「시작하기 단계 화면」 채택 — 전면 창(시안 A)에서 바꿈)
// 「우리가 등급을 올려주면 결제해야만 이용 · 로그인하자마자 다른 버튼은 눌리지 않고 결제 페이지에서 결제 · 그 등급으로 다룰 수 있는 작품은 볼 수 있게 · 14일 없음」
// 같은 날 후속 지시: 「가입비 결제 직후 카드(결제수단) 등록까지 해야 이용 시작 — 잔여일·월 구독료가 빠지지 않게」 → 단계 두 개
//   stage 'signup_fee'(가입비 결제 → /billing/pay?kind=signup_fee) · 'card'(카드 등록 → /billing?register=1, 멤버십 화면이 등록 창을 바로 연다).
//   🔒 카드 등록 창(PaymentSheet)은 멤버십 화면에서만 연다 — 다른 Chakra 모달 위에서 열면 포커스 가둠이 토스 카드 입력칸을 막는다(PaymentSheet 주석).
// 판정은 서버 tier_grant_payment_gate() 하나(supabase/migrations/20261028110000). 여기서는 받은 값을 화면 모양으로만 바꾼다.
// 🔒 supabase import 금지 — 테스트가 .env 없이 돌아야 한다. 금액은 서버 값 그대로(여기서 계산하지 않는다).
import { tt, ttl } from "../i18n/tt";
import { formatKrw, shortDateLabel } from "./billingState";

/** 잠겨 있어도 /start 로 보내지 않는 경로 — 결제(/billing/pay·/billing/success·/billing/fail 포함) · 문의 · 약관 동의 기록 · 환영 여정. 늘리면 docs/pages/main/Billing.md 「결제 전 잠금」도 같이 */
export const GATE_EXEMPT_PATHS = ["/billing", "/support", "/policies", "/welcome"];
export function isTierGrantGateExempt(pathname) {
  const p = String(pathname || "");
  return GATE_EXEMPT_PATHS.some((base) => p === base || p.startsWith(`${base}/`));
}

export const START_PATH = "/start";
export const SIGNUP_PAY_PATH = "/billing/pay?kind=signup_fee";
export const CARD_REGISTER_PATH = "/billing?register=1";

const BAND_KO = { D: "실버", C: "골드", B: "플래티넘", A: "다이아몬드", S: "마스터" };
const LOCKED_ORDER = ["C", "B", "A", "S"];

/** 등급 글자 → 화면 이름(언어별) */
export function gateBandName(band) {
  return BAND_KO[band] ? ttl(BAND_KO[band]) : "";
}

/** 서버 응답을 화면이 쓰는 모양으로. 이상한 값(빈 배열·null·문자열)은 잠그지 않는다 — 조회 실패로 회원을 막지 않는다 */
export function normalizeGate(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw) || raw.gated !== true) return { gated: false };
  const works = Array.isArray(raw.works) ? raw.works.filter((w) => w && w.id != null && w.title) : [];
  return {
    gated: true,
    stage: raw.stage === "card" ? "card" : "signup_fee",
    // grant = 레이블리가 시작 등급을 지정 · promotion = 루키 승급 심사 승인(대표 후속 지시 ② 2026-10-20)
    // tier = 월 확정 등으로 정식 등급이 된 신규 회원(가입비 먼저 · 대표 2026-10-23 · 20261117100000)
    source: raw.source === "promotion" ? "promotion" : raw.source === "tier" ? "tier" : "grant",
    band: raw.band || "D",
    tierName: gateBandName(raw.band || "D"),
    endsAt: raw.ends_at || null,
    today: raw.today || null,
    signupFee: Number(raw.signup_fee || 0),
    monthlyFee: Number(raw.monthly_fee || 0),
    remainderFee: Number(raw.remainder_fee || 0),
    coverage: raw.coverage && typeof raw.coverage === "object" ? raw.coverage : {},
    works,
    lockedByBand: raw.locked_by_band && typeof raw.locked_by_band === "object" ? raw.locked_by_band : {},
  };
}

function addDaysYmd(ymd, n) {
  const d = new Date(`${String(ymd).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function firstOfNextMonthYmd(ymd) {
  const d = new Date(`${String(ymd).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString().slice(0, 10);
}

/**
 * 「오늘 결제하면」 세 줄 — 가입비(포함 기간) · 잔여일 일할 · 월 멤버십 구독료 시작일.
 * 규칙은 서버 billing_signup_coverage · 청구 _shared/charge.ts 와 같다(가입비 300,000 = 결제일부터 한 달 · 그 뒤 말일까지 일할 · 다음 달 1일부터 월).
 * @returns {{key:string, when:string, amount:string, note:string}[]}
 */
export function paymentLines(gate) {
  if (!gate?.gated) return [];
  const c = gate.coverage || {};
  const tier = gate.tierName;
  const card = gate.stage === "card";
  const lines = [card ? {
    key: "signup",
    when: tt("tgp.lineSignupPaid", "가입비"),
    amount: tt("tgp.lineSignupPaidAmount", "결제 완료"),
    note: c.covers_until
      ? tt("tgp.lineSignupPaidNote", "{{v0}}까지 이용 포함 · 이 기간에는 청구 없음", { v0: shortDateLabel(c.covers_until) })
      : tt("tgp.lineSignupNoteMonth", "가입비 · 결제일부터 한 달 이용 포함"),
  } : {
    key: "signup",
    when: tt("tgp.lineToday", "오늘 결제"),
    amount: formatKrw(gate.signupFee),
    note: c.covers_until
      ? tt("tgp.lineSignupNote", "가입비 · 오늘부터 {{v0}}까지 이용 포함", { v0: shortDateLabel(c.covers_until) })
      : tt("tgp.lineSignupNoteMonth", "가입비 · 결제일부터 한 달 이용 포함"),
  }];
  const remDays = Number(c.remainder_days || 0);
  if (remDays > 0 && c.remainder_from) {
    lines.push({
      key: "remainder",
      when: shortDateLabel(c.remainder_from),
      amount: formatKrw(gate.remainderFee),
      note: card
        ? tt("tgp.lineRemainderNoteCard", "{{v0}}~{{v1}} {{v2}}일분 · {{v3}} 월 {{v4}} 기준 일할 · 등록한 카드로 자동 결제", {
          v0: shortDateLabel(c.remainder_from), v1: shortDateLabel(c.remainder_until), v2: remDays, v3: tier, v4: formatKrw(gate.monthlyFee),
        })
        : tt("tgp.lineRemainderNote", "{{v0}}~{{v1}} {{v2}}일분 · {{v3}} 월 {{v4}} 기준 일할", {
          v0: shortDateLabel(c.remainder_from), v1: shortDateLabel(c.remainder_until), v2: remDays, v3: tier, v4: formatKrw(gate.monthlyFee),
        }),
    });
  }
  const monthlyFrom = remDays > 0 && c.remainder_from ? firstOfNextMonthYmd(c.remainder_from) : (c.covers_until ? addDaysYmd(c.covers_until, 1) : null);
  if (gate.monthlyFee > 0) {
    lines.push({
      key: "monthly",
      when: monthlyFrom ? tt("tgp.lineMonthlyFrom", "{{v0}}부터", { v0: shortDateLabel(monthlyFrom) }) : tt("tgp.lineMonthlyNext", "그다음 달부터"),
      amount: tt("tgp.lineMonthlyAmount", "매월 {{v0}}", { v0: formatKrw(gate.monthlyFee) }),
      note: tt("tgp.lineMonthlyNote", "그달 1일에 확정된 등급의 월 멤버십 구독료"),
    });
  }
  return lines;
}

/** 「등급이 오르면 열리는 작품」 — 골드부터 26편 · 플래티넘부터 6편 … */
export function lockedSummary(lockedByBand) {
  const parts = LOCKED_ORDER
    .filter((b) => Number(lockedByBand?.[b]) > 0)
    .map((b) => ({ band: b, count: Number(lockedByBand[b]), label: tt("tgp.lockedPart", "{{v0}}부터 {{v1}}편", { v0: gateBandName(b), v1: Number(lockedByBand[b]) }) }));
  return { total: parts.reduce((n, p) => n + p.count, 0), parts, text: parts.map((p) => p.label).join(" · ") };
}

/** 작품 카드의 작은 꼬리표 — 지정 등급에서 처음 열리는 작품(「골드부터」)과 브랜드 채널 전용만 */
export function workTag(work, band) {
  if (work?.brand_only) return tt("tgp.tagBrandOnly", "브랜드 채널 전용");
  const min = work?.min_creator_band;
  if (min && min !== "D" && min === band) return tt("tgp.tagFrom", "{{v0}}부터", { v0: gateBandName(min) });
  return null;
}

/**
 * 시작하기 페이지의 세 단계 — ① 가입비 결제 ② 카드 등록 ③ 작품 신청 시작.
 * state: current(지금 할 일 — 금액·버튼을 펼친다) · done(끝남) · todo(다음) · locked(두 단계 뒤)
 */
export function startSteps(gate) {
  if (!gate?.gated) return [];
  const card = gate.stage === "card";
  const c = gate.coverage || {};
  return [
    {
      key: "signup_fee",
      state: card ? "done" : "current",
      title: card ? tt("tgs.signupDone", "가입비 결제 완료") : tt("tgp.stepSignup", "가입비 결제"),
      sub: card && c.covers_until
        ? tt("tgp.lineSignupPaidNote", "{{v0}}까지 이용 포함 · 이 기간에는 청구 없음", { v0: shortDateLabel(c.covers_until) })
        : null,
    },
    {
      key: "card",
      state: card ? "current" : "todo",
      title: tt("tgp.stepCard", "카드 등록"),
      sub: card ? null : tt("tgs.cardTodoNote", "가입비 결제 뒤 · 등록하는 날에는 결제되지 않아요"),
    },
    {
      key: "start",
      state: "locked",
      title: tt("tgs.startTitle", "작품 신청 시작"),
      sub: card
        ? tt("tgs.startNoteCard", "카드 등록이 끝나면 홈과 모든 메뉴가 열려요")
        : tt("tgs.startNote", "두 단계가 끝나면 홈과 모든 메뉴가 열려요"),
    },
  ];
}

/** 지금 단계의 버튼 — 가입비 결제(/billing/pay) 또는 카드 등록(/billing?register=1) */
export function stepCta(gate) {
  if (!gate?.gated) return null;
  const card = gate.stage === "card";
  return {
    label: card ? tt("tgp.ctaCard", "카드 등록하기") : tt("tgp.cta", "가입비 결제하기"),
    path: card ? CARD_REGISTER_PATH : SIGNUP_PAY_PATH,
    hint: card
      ? tt("tgp.ctaHintCard", "등록하는 날에는 결제되지 않아요 · 이미 결제한 기간이 끝나는 날부터 자동 결제")
      : tt("tgp.ctaHintSignup", "결제 뒤 카드를 등록하면 바로 작품 신청·다운로드를 할 수 있어요"),
  };
}

/** 카드 단계의 금액 줄 — 오늘 0원 · 잔여일 일할(카드 자동) · 다음 달부터 매월 */
export function cardStepLines(gate) {
  if (!gate?.gated || gate.stage !== "card") return [];
  return [
    { key: "today", when: tt("tgs.today", "오늘"), amount: tt("tgs.todayZero", "0원 · 결제 없음"), note: tt("tgs.todayZeroNote", "카드를 등록하는 날에는 결제되지 않아요") },
    ...paymentLines(gate).filter((l) => l.key !== "signup"),
  ];
}

/** 시작하기 페이지 머리 문구 */
export function startCopy(gate, { native = false } = {}) {
  if (!gate?.gated) return null;
  const tier = gate.tierName;
  const c = gate.coverage || {};
  let intro;
  // 🔒 앱에서는 금액·「가입비」·결제 링크를 말하지 않는다(애플 인앱결제 규정 — AppBillingNotice 와 같은 원칙: 「웹에서 관리해요」 + laeebly.io 글자만)
  if (native) intro = tt("tgp.bodyNative", "멤버십 시작은 웹에서 해요. 시작하면 앱에서도 바로 작품을 신청할 수 있어요. 그 전에는 작품 신청·다운로드 등 다른 기능을 쓸 수 없어요.");
  else if (gate.stage === "card") intro = tt("tgs.introCard", "한 단계만 남았어요. 카드를 등록하면 바로 작품을 신청할 수 있어요.");
  else if (gate.source === "promotion") intro = tt("tgs.introPromotion", "승급 심사를 통과해 {{v0}} 등급이 됐어요. 두 단계만 마치면 바로 작품을 신청할 수 있어요.", { v0: tier });
  else if (gate.source === "tier") intro = tt("tgs.introTier", "{{v0}} 등급이 확정됐어요. 두 단계만 마치면 바로 작품을 신청할 수 있어요.", { v0: tier });
  else if (gate.endsAt) intro = tt("tgs.introGrantUntil", "{{v0}} 등급으로 지정됐어요({{v1}}까지). 두 단계만 마치면 바로 작품을 신청할 수 있어요.", { v0: tier, v1: shortDateLabel(gate.endsAt) });
  else intro = tt("tgs.introGrant", "{{v0}} 등급으로 지정됐어요. 두 단계만 마치면 바로 작품을 신청할 수 있어요.", { v0: tier });
  return {
    title: tt("tgs.title", "레이블리 시작하기"),
    intro,
    signupAmountNote: c.covers_until
      ? tt("tgs.signupAmountNote", "오늘부터 {{v0}}까지 {{v1}} 이용 포함", { v0: shortDateLabel(c.covers_until), v1: tier })
      : tt("tgp.lineSignupNoteMonth", "가입비 · 결제일부터 한 달 이용 포함"),
    badgeNow: tt("tgs.badgeNow", "지금 할 일"),
    worksKicker: tt("tgs.previewKicker", "미리 보기"),
    worksTitle: tt("tgp.worksTitle", "{{v0}} 등급으로 신청할 수 있는 작품 {{v1}}편", { v0: tier, v1: gate.works.length }),
  };
}
